import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';

const IDLE_LIMIT_MS = 15 * 60 * 1000;
const WARNING_BEFORE_MS = 2 * 60 * 1000;
const ACTIVITY_KEY = 'vl_last_activity';
const EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export const IdleTimeoutGuard = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(WARNING_BEFORE_MS / 1000));
  const lastActivityRef = useRef(Date.now());
  const warningRef = useRef(false);

  const markActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch { /* storage pieno/bloccato */ }
  }, []);

  const keepAlive = () => {
    warningRef.current = false;
    setWarning(false);
    markActivity();
  };

  const forceLogout = useCallback(async (expired) => {
    warningRef.current = false;
    setWarning(false);
    await logout();
    if (expired) toast.error('Sessione scaduta per inattività. Accedi di nuovo.');
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;

    markActivity();

    let throttle = 0;
    const onActivity = () => {
      if (warningRef.current) return; // durante l'avviso solo i pulsanti resettano il timer
      const now = Date.now();
      if (now - throttle < 1000) return;
      throttle = now;
      markActivity();
    };
    EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    const onStorage = (e) => {
      if (e.key === ACTIVITY_KEY && e.newValue) {
        lastActivityRef.current = Number(e.newValue);
        warningRef.current = false;
        setWarning(false);
      }
    };
    window.addEventListener('storage', onStorage);

    const tick = setInterval(() => {
      const stored = Number(localStorage.getItem(ACTIVITY_KEY) || 0);
      const last = Math.max(lastActivityRef.current, stored || 0);
      const idle = Date.now() - last;

      if (idle >= IDLE_LIMIT_MS) {
        forceLogout(true);
      } else if (idle >= IDLE_LIMIT_MS - WARNING_BEFORE_MS) {
        warningRef.current = true;
        setWarning(true);
        setSecondsLeft(Math.max(0, Math.ceil((IDLE_LIMIT_MS - idle) / 1000)));
      }
    }, 1000);

    return () => {
      EVENTS.forEach(e => window.removeEventListener(e, onActivity));
      window.removeEventListener('storage', onStorage);
      clearInterval(tick);
    };
  }, [isAuthenticated, markActivity, forceLogout]);

  if (!isAuthenticated) return null;

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');

  return (
    <Dialog open={warning} onOpenChange={(o) => { if (!o) keepAlive(); }}>
      <DialogContent className="max-w-md" data-testid="idle-warning-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock size={22} weight="duotone" className="text-destructive" /> Sessione in scadenza
          </DialogTitle>
          <DialogDescription>
            Per sicurezza verrai disconnesso automaticamente per inattività.
          </DialogDescription>
        </DialogHeader>
        <div className="text-center py-4">
          <p className="font-mono text-4xl font-semibold text-destructive" data-testid="idle-countdown">{mm}:{ss}</p>
          <p className="text-sm text-foreground/60 mt-2">Vuoi mantenere aperta la sessione?</p>
        </div>
        <div className="flex gap-2">
          <Button className="flex-1" onClick={keepAlive} data-testid="idle-stay-btn">Rimani connesso</Button>
          <Button variant="outline" className="flex-1" onClick={() => forceLogout(false)} data-testid="idle-logout-btn">Esci ora</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IdleTimeoutGuard;
